import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSectionView, type SectionId } from '@/components/feature/SectionViewProvider';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { goToSection } = useSectionView();
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Close menu on outside click
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [mobileOpen]);

  const navLinks: { label: string; sectionId: SectionId }[] = [
    { label: 'Our Services', sectionId: 'services' },
    { label: 'Testimonials', sectionId: 'testimonials' },
    { label: 'Contact Us', sectionId: 'contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300">
      {/* Navbar backdrop lives on its own inner layer so the <nav> element
          never gets a backdrop-filter. A backdrop-filter on <nav> would turn
          it into a containing block for the fixed mobile menu overlay, which
          collapsed the blur overlay into the header strip on non-home views.
          Keeping it on a child layer preserves the exact same look everywhere. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-[background-color,backdrop-filter] duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-sm'
            : 'bg-transparent backdrop-blur-none'
        }`}
      />
      <div className="relative w-full px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-1.5 shrink-0">
            <div className="w-14 h-14 flex items-center justify-center overflow-hidden rounded-md -translate-x-4 md:-translate-x-1.5">
              <img
                src="/assets/logo.png"
                alt="SettleSpace"
                className="w-full h-full object-contain"
              />
            </div>
            <img
              src="/assets/wordmark.png"
              alt="SettleSpace"
              className="h-9 md:h-10 w-auto object-contain -ml-8 md:-ml-3 opacity-80 md:opacity-100"
            />
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {navLinks.map((link) => (
              <button
                key={link.sectionId}
                onClick={() => goToSection(link.sectionId)}
                className="relative text-sm font-medium text-foreground-800 hover:text-foreground-950 whitespace-nowrap pb-0.5 bg-gradient-to-r from-foreground-950 to-foreground-950 bg-no-repeat [background-size:0%_1px] hover:[background-size:100%_1px] bg-[position:0_100%] transition-all duration-300 cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </div>

          {/* Mobile Hamburger - wrapped in relative container for tray positioning */}
          <div className="md:hidden relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center"
              aria-label="Toggle menu"
            >
              <i
                className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl text-foreground-950`}
              />
            </button>

            {/* Floating Tray */}
            <div className={`fixed inset-0 z-40 flex items-start justify-end ${mobileOpen ? 'pointer-events-auto visible' : 'pointer-events-none invisible delay-200'}`}>
              {/* Subtle backdrop */}
              <div
                className={`absolute inset-0 transition-[background-color,backdrop-filter] duration-200 ease-out ${mobileOpen ? 'bg-foreground-950/15 backdrop-blur-[2px]' : 'bg-transparent backdrop-blur-none'}`}
                onClick={() => setMobileOpen(false)}
              />
              {/* Tray */}
              <div
                className={`relative mt-[4.25rem] mr-4 w-60 bg-background-50 border border-background-200/50 shadow-[0_8px_30px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden transition-[opacity,transform] duration-200 ease-out origin-top-right ${
                  mobileOpen
                    ? 'opacity-100 scale-100 translate-y-0'
                    : 'opacity-0 scale-95 -translate-y-1'
                }`}
              >
                <div className="py-1">
                  {navLinks.map((link, index) => (
                    <div key={link.sectionId}>
                      <button
                        onClick={() => {
                          goToSection(link.sectionId);
                          setMobileOpen(false);
                        }}
                        className="w-full text-left px-5 py-3.5 text-sm font-medium text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
                      >
                        {link.label}
                      </button>
                      {index < navLinks.length - 1 && (
                        <div className="mx-4 border-b border-background-200/40" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Remove old Mobile Menu block entirely — replaced by floating tray above */}
    </nav>
  );
}